import React, { useState, useEffect } from 'react';
import {
  Plus,
  Mail,
  Clock,
  CheckCircle,
  Copy,
  ExternalLink,
  Users,
  Eye
} from 'lucide-react';
import ApiService from '../services/ApiService';

const ContractorInviteManager = () => {
  const [invites, setInvites] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    xeroContactId: '',
    contractorName: '',
    email: '',
    notes: ''
  });

  useEffect(() => {
    loadInvites();
    loadContacts();
  }, []);

  const loadInvites = async () => {
    try {
      console.log('📧 Loading contractor invites...');
      const inviteData = await ApiService.apiCall('/contractor/invites');
      setInvites(inviteData);
      console.log('✅ Loaded', inviteData.length, 'contractor invites');
    } catch (error) {
      console.error('Error loading invites:', error);
      setInvites([]); // Set empty array on error
    }
  };

  const loadContacts = async () => {
    try {
      const contactsData = await ApiService.apiCall('/contactenhancement/enhanced-contacts');
      const supplierContacts = contactsData.filter(c => c.isSupplier || c.IsSupplier);
      setContacts(supplierContacts);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('📧 Creating contractor invite:', formData);
      const result = await ApiService.apiCall('/contractor/create-invite', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      
      if (result.success) {
        const realInviteUrl = result.invite.inviteUrl;
        alert(`Invite created for ${formData.contractorName}!\n\nInvite URL: ${realInviteUrl}\n\n(Copy this link and send to the contractor)`);
        
        setShowCreateModal(false);
        setFormData({ xeroContactId: '', contractorName: '', email: '', notes: '' });
        await loadInvites();
      } else {
        throw new Error(result.message || 'Failed to create invite');
      }
    } catch (error) {
      console.error('Error creating invite:', error);
      alert('Failed to create invite: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-populate contractor name when contact is selected
    if (name === 'xeroContactId') {
      const selectedContact = contacts.find(c => c.contactID === value);
      if (selectedContact) {
        setFormData(prev => ({
          ...prev,
          contractorName: selectedContact.name,
          email: selectedContact.emailAddress || ''
        }));
      }
    }
  };

  const copyInviteUrl = (inviteUrl) => {
    navigator.clipboard.writeText(inviteUrl);
    alert('Invite URL copied to clipboard!');
  };

  const handleViewPortal = async (invite) => {
    try {
      const result = await ApiService.apiCall(`/contractor/admin-preview-token?email=${encodeURIComponent(invite.email)}`);
      const previewUrl = `${window.location.origin}/contractor/preview?token=${encodeURIComponent(result.token)}`;
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      alert(`Cannot open portal preview: ${error.message}`);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <Users className="mr-2" />
            Contractor Invites
          </h2>
          <p className="text-gray-600">Invite contractors to access their work order portal</p>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Invite Contractor
        </button>
      </div>

      {/* Invites List */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Invitations</h3>
        </div>
        <div className="p-6">
          {invites.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No contractor invites yet. Create your first invite to get started!
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contractor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invites.map((invite) => (
                    <tr key={invite.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{invite.contractorName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {invite.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          invite.status === 'Accepted' ? 'bg-green-100 text-green-800' :
                          invite.status === 'Expired' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {invite.status === 'Accepted' && <CheckCircle className="w-3 h-3 mr-1" />}
                          {invite.status === 'Pending' && <Clock className="w-3 h-3 mr-1" />}
                          {invite.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(invite.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(invite.expiresAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          {invite.status === 'Accepted' && (
                            <button
                              onClick={() => handleViewPortal(invite)}
                              className="text-indigo-600 hover:text-indigo-900 p-1"
                              title="Preview contractor portal"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => copyInviteUrl(invite.inviteUrl)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Copy Invite URL"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <a
                            href={invite.inviteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Open Invite URL"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Invite Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Invite Contractor</h3>
                <button 
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <form onSubmit={handleCreateInvite} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Contractor (from Xero Contacts) *
                  </label>
                  <select
                    name="xeroContactId"
                    value={formData.xeroContactId}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a contractor...</option>
                    {contacts.map(contact => (
                      <option key={contact.contactID} value={contact.contactID}>
                        {contact.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contractor Name *
                  </label>
                  <input
                    type="text"
                    name="contractorName"
                    value={formData.contractorName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Contractor's full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="contractor@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Any additional notes for this contractor..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    {loading ? 'Creating...' : 'Create Invite'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorInviteManager;
